/**
 * CampaignCard Component
 *
 * Purpose: Display a single campaign in card format
 * WHO: Users viewing campaign lists
 * WHAT: Show campaign info with status, participant count, GM name
 * WHERE: Dashboard, campaign lists
 * WHY: Clean, clickable interface for navigating to campaigns
 *
 * INTEGRATION POINT: Receives campaign data from API
 * Contract: shared/contracts/campaign/v1/campaign-list.yaml
 */

import React from "react";
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  HStack,
  VStack,
  Badge,
  Icon,
  Divider,
  Button,
  useColorMode,
} from "@chakra-ui/react";
import { Users, Calendar, Zap } from "lucide-react";

interface CampaignData {
  campaign_id: string;
  campaign_name: string;
  description?: string;
  status?: "active" | "archived";
  is_active?: boolean;
  participant_count?: number;
  updated_at?: string;
  gm_name?: string;
  created_at?: string;
}

interface CampaignCardProps {
  campaign: CampaignData;
  onSelect: () => void;
}

export const CampaignCard: React.FC<CampaignCardProps> = ({
  campaign,
  onSelect,
}) => {
  const { colorMode } = useColorMode();

  // Determine campaign status
  const isArchived = campaign.status === "archived" || !campaign.is_active;
  const statusColor = isArchived ? "gray" : "green";
  const statusText = isArchived ? "Archived" : "Active";

  // Format last updated date
  const lastUpdated = campaign.updated_at
    ? new Date(campaign.updated_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : campaign.created_at
      ? new Date(campaign.created_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })
      : "Unknown";

  // Participant count with default
  const participants = campaign.participant_count || 0;

  return (
    <Card
      onClick={onSelect}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        boxShadow: "lg",
        transform: "translateY(-2px)",
        borderColor: "blue.500",
      }}
      borderWidth={1}
      borderColor={colorMode === "light" ? "gray.200" : "gray.600"}
    >
      {/* Card Header with Title and Status Badge */}
      <CardHeader pb={2}>
        <HStack justify="space-between" align="start" w="full">
          <VStack align="start" spacing={1} flex={1}>
            <Heading
              size="md"
              noOfLines={2}
              _hover={{ color: "blue.500" }}
              transition="color 0.2s"
            >
              {campaign.campaign_name}
            </Heading>
          </VStack>
          <Badge colorScheme={statusColor} flexShrink={0}>
            {statusText}
          </Badge>
        </HStack>
      </CardHeader>

      <Divider />

      {/* Card Body with Description and Meta */}
      <CardBody>
        <VStack spacing={4} align="start" w="full">
          {/* Description */}
          {campaign.description && (
            <Text
              fontSize="sm"
              color="gray.600"
              _dark={{ color: "gray.400" }}
              noOfLines={2}
            >
              {campaign.description}
            </Text>
          )}

          {/* Metadata Row 1: Participants and GM */}
          <HStack fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }} spacing={4} w="full">
            <HStack spacing={1} flex={1}>
              <Icon as={Users} w={4} h={4} />
              <Text>
                <strong>{participants}</strong> player{participants !== 1 ? "s" : ""}
              </Text>
            </HStack>

            {campaign.gm_name && (
              <Text fontSize="xs">GM: <strong>{campaign.gm_name}</strong></Text>
            )}
          </HStack>

          {/* Metadata Row 2: Updated Date */}
          <HStack fontSize="xs" color="gray.500" _dark={{ color: "gray.500" }} spacing={2}>
            <Icon as={Calendar} w={3} h={3} />
            <Text>Updated: {lastUpdated}</Text>
          </HStack>

          {/* Archived notice */}
          {isArchived && (
            <Box
              bg="gray.100"
              p={2}
              borderRadius="md"
              w="full"
              _dark={{ bg: "gray.700" }}
            >
              <HStack fontSize="xs" spacing={1}>
                <Icon as={Zap} w={3} h={3} opacity={0.6} />
                <Text opacity={0.7}>Campaign is archived</Text>
              </HStack>
            </Box>
          )}
        </VStack>
      </CardBody>

      {/* Hidden button for keyboard navigation */}
      <Box display="none">
        <Button onClick={onSelect}>View Campaign</Button>
      </Box>
    </Card>
  );
};

export default CampaignCard;
